import type { RiskRecognitionHighlight } from '@cobliteam/api-lib/src/dashboard/riskRecognitions/Types';
import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Tag from '@cobliteam/ui/components/Tag';
import Typography from '@cobliteam/ui/components/Typography';
import Icon from '@cobliteam/ui/icons';
import { cn } from '@cobliteam/ui/utils';
import ShineBorder from '@cobliteam/ui-business/components/border/ShineBorder';
import AIButton from '../AIButton';
import { formatRiskRecognitionCardDisplay, getRiskRecognitionIcon } from './formatRiskRecognitionCardDisplay';
import RiskHighlightText, { renderRiskHighlight } from './RiskHighlightText';

interface RiskRecognitionCardContentProps {
  highlight: RiskRecognitionHighlight;
  actionLabel?: string | null;
  dismissLabel?: string | null;
  onDismiss: () => void;
  onAction: () => void;
  className?: string;
}

// Caixa visual do card de risco (badge de urgência + fechar, título, descrição, ações). Compartilhada
// entre o card flutuante (/monitoring) e o toast (sonner, qualquer página) pra terem o MESMO layout.
const RiskRecognitionCardContent = ({ highlight, actionLabel, dismissLabel, onDismiss, onAction, className }: RiskRecognitionCardContentProps) => {
  const { t } = useTranslation('ai');
  const { headline, description, location } = formatRiskRecognitionCardDisplay(highlight);
  const iconName = getRiskRecognitionIcon(highlight);
  const actionText = actionLabel ?? t('risk_recognition.action');
  const dismissText = dismissLabel ?? t('risk_recognition.dismiss');

  return (
    <div
      className={cn(
        'relative flex w-full flex-col gap-[9px] rounded-2xl border-danger-5 border-solid bg-[#fcfdfe] p-[14px] shadow-[0_0_0_3px_rgba(254,226,226,0.70),0_12px_28px_-16px_rgba(185,28,28,0.48),0_6px_14px_-10px_rgba(15,23,42,0.18)]',
        className
      )}
    >
      <ShineBorder shineColor={['#FECACA', '#FCA5A5', '#EF4444', '#DC2626', '#F87171', '#FECACA']} borderWidth={2} duration={6} />

      <div className='flex w-full items-center justify-between'>
        <div className='flex items-center gap-2'>
          <span className='flex size-9 shrink-0 items-center justify-center rounded-full bg-danger-3 text-danger-9' aria-hidden>
            <Icon name={iconName} size='sm' />
          </span>
          <Tag variant='surface' color='danger' size='sm' label={t('risk_recognition.badge')} icon={<Icon name='triangle-alert' size='xs' />} />
        </div>
        <button
          type='button'
          onClick={onDismiss}
          aria-label={dismissText}
          className='flex size-6 cursor-pointer items-center justify-center rounded-lg text-[#737373] transition-colors hover:bg-neutral-100'
        >
          <Icon name='x' size='sm' />
        </button>
      </div>
      <Typography variant='description-title' className='break-words text-danger-10 leading-[1.35] [overflow-wrap:anywhere]'>
        {renderRiskHighlight(headline, 'font-semibold')}
      </Typography>
      <RiskHighlightText text={description} variant='description-list-default' className='break-words text-gray-11 leading-[1.45] [overflow-wrap:anywhere]' />
      {location ? (
        <div className='flex items-start gap-1.5 text-gray-8'>
          <Icon name='map-pin' size='xs' className='mt-0.5 shrink-0' aria-hidden />
          <Typography variant='caption-xs' className='break-words leading-[1.4] [overflow-wrap:anywhere]'>
            {location}
          </Typography>
        </div>
      ) : null}
      <div className='flex items-center justify-between gap-3 pt-1'>
        <Button variant='ghost' color='default' size='sm' onClick={onDismiss}>
          {dismissText}
        </Button>
        <AIButton size='sm' label={actionText} shape='cta' showTooltip={false} onClick={onAction} />
      </div>
    </div>
  );
};

export default RiskRecognitionCardContent;
